// the following types are taken from:
// https://listenfield.gitlab.io/products/farmai/agroapi/api/#tag/ActivityType/paths/~1activity_types/get
type SoilType = 'sandy' | 'sandy_loam' | 'sandy_clay_loam' | 'sandy_clay' | 'loamy_sand' | 'loamy' | 'clay_loam' | 'clay' | 'silty_clay' | 'silty_clay_loam' | 'silty_loam' | 'silt' | 'no_data' | 'other' | null;
type TimingDaysBeforePlantingType = 'less_than_equal_30' | 'greater_than_30' | null;
type LandPreparationMethodType = 'plowing' | 'disking' | 'tilling' | null;
type PestDiseaseMethod = 'drone' | 'sprayer' | 'human' | null;
type PestDiseaseType = 'chemical_pesticide' | 'biocontrol' | null;
interface ISoilObservationProperties {
  soil_type: SoilType;
}
interface IPlantingProperties {
  seed_usage: number;
  seed_usage_unit: {
    label?: string;
    value: string;
  };
  plantingMethod: {
    label?: string;
    value: string;
  };
}
interface IHarvestingProperties {
  harvest: number | null;
}
interface IWaterSupplyProperties {
  water_regime_before_planting: string;
  water_regime_while_planting: string;
}
interface ILandPreparationProperties {
  landPreparationMethod: LandPreparationMethodType;
}
interface IInspectionProperties {
  pestDiseaseType: PestDiseaseType;
  pestDiseaseMethod: PestDiseaseMethod;
  pestDiseaseProductName: string;
}
interface IStrawIncorporationProperties {
  timingDaysBeforePlanting: TimingDaysBeforePlantingType;
}
interface ICoverCropPlantingProperties {
  type: string;
}
type Rating = number | null;
type EstimatedProduction = number | null;
type EstimatedAreaLoss = number | null;
type CauseOfLoss = 'BY_FLOOD' | 'BY_DROUGHT' | 'BY_DISEASE' | 'BY_WEED' | 'BY_OTHER' | null;
interface IInspectionProperties {
  rating: Rating;
  estimated_production: EstimatedProduction;
  estimated_area_loss: EstimatedAreaLoss;
  cause_of_loss: CauseOfLoss;
}
type FertilizerType = 'chemical' | 'chicken_manure' | 'cow_manure' | 'other_organic' | 'slow_release' | 'lime' | 'dolomite' | null;
type FertilizerMethod = 'machine_broadcast' | 'drone' | 'sprayer' | 'human' | 'drip' | null;
interface IFertilizationProperties {
  fertilizerType: FertilizerType;
  fertilizerMethod: FertilizerMethod;
  nitrogen?: number;
  phosphorus: number | null;
  potassium: number | null;
  fertilizerAmount: number | null;
  fertilzierAmount_unit: 'kg/m2' | 'L/m2';
}
type StrawManagementOperation = 'not_considered' | 'tillage_only' | 'tillage_laser_land_leveling' | 'tillage_straw_bailing' | 'tillage_straw_bailing_laser_land_leveling' | null;
type StrawManagementType = 'not_considered' | 'burning' | null;
interface IStrawManagementProperties {
  type: StrawManagementType;
  operation: StrawManagementOperation;
}
export type IElements = ISoilObservationProperties | IHarvestingProperties | IPlantingProperties | IInspectionProperties | IFertilizationProperties | IWaterSupplyProperties | IStrawManagementProperties | ILandPreparationProperties | IInspectionProperties | IStrawIncorporationProperties | ICoverCropPlantingProperties;
interface IRawSchemaProperty {
  type: string;
  minLength?: number;
  enum?: string[];
}
interface IBasicFieldSchema extends IRawSchemaProperty {}
interface IRawSchemaProperties {
  [key: string]: IRawSchemaProperty;
}
interface ICustomFieldSchema {
  label: string;
  custom_unit: {
    scope: string;
    enum: string[];
  };
}
interface ILayout {
  type: 'VerticalLayout' | 'HorizontalLayout';
  elements: Record<string, IAgroSchemaElement>;
}
type IAgroSchemaElement = ICustomFieldSchema | IBasicFieldSchema | ILayout;
export type IAgroSchema = ILayout;

/**
 * Transformer for fertilization properties.
 * Receives raw properties and returns a record of schema elements.
 */
export const fertilizerTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (['nitrogen', 'potassium', 'phosphorus'].includes(key) && !acc.hasOwnProperty('HorizontalLayout')) {
    acc['HorizontalLayout'] = {
      type: 'HorizontalLayout',
      elements: {} as Record<string, IAgroSchemaElement>
    };
  }
  if (['nitrogen', 'potassium', 'phosphorus'].includes(key)) {
    ;
    (acc['HorizontalLayout'] as ILayout).elements[key] = field;
    return acc;
  }
  if (key === 'type') {
    acc['fertilizerType'] = field;
    return acc;
  }
  if (key === 'method') {
    acc['fertilizerMethod'] = field;
    return acc;
  }
  if (key === 'amount_unit') {
    acc['fertilizerAmount_unit'] = field;
    return acc;
  }
  if (key === 'amount') {
    acc['fertilizerAmount'] = {
      label: 'actualAmount',
      custom_unit: {
        scope: 'fertilizerAmount_unit',
        enum: properties.amount_unit?.enum || []
      }
    } as ICustomFieldSchema;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);

/**
 * Transformer for planting properties.
 * Generates custom fields for seed usage.
 */
export const plantingTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'seed_usage') {
    acc['seed_usage'] = {
      label: 'amountOfSeeds',
      custom_unit: {
        scope: 'seed_usage_unit',
        enum: properties.seed_usage_unit?.enum || []
      }
    } as ICustomFieldSchema;
    acc['seed_usage_computed'] = {
      label: 'computed'
    } as ICustomFieldSchema;
    return acc;
  }
  if (key === 'method') {
    acc['plantingMethod'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);

/**
 * Transformer for land preparation properties.
 * Generates custom fields for land preparation method.
 */
export const landPreparationTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'method') {
    acc['landPreparationMethod'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);

/**
 * Transformer for straw incorporation properties.
 * Generates custom fields for straw incorporation method.
 */
export const strawIncorporationTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'timing_days_before_planting') {
    acc['timingDaysBeforePlanting'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);

/**
 * Transformer for straw management properties.
 * Generates custom fields for straw management  method.
 */
export const strawManagementTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'type') {
    acc['strawManagementType'] = field;
    return acc;
  }
  if (key === 'operation') {
    acc['strawManagementOperation'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);

/**
 * Transformer for pest disease properties.
 * Generates custom fields for pest disease  method.
 */
export const pestDiseaseTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'method') {
    acc['pestDiseaseMethod'] = field;
    return acc;
  }
  if (key === 'type') {
    acc['pestDiseaseType'] = field;
    return acc;
  }
  if (key === 'product_name') {
    acc['pestDiseaseProductName'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);
export const coverCropPlantingTransformer = (properties: IRawSchemaProperties): Record<string, IAgroSchemaElement> => Object.entries(properties).reduce((acc, [key, field]) => {
  if (key === 'type') {
    acc['coverCropType'] = field;
    return acc;
  }
  acc[key] = field;
  return acc;
}, {} as Record<string, IAgroSchemaElement>);
export const withSchemas = self => ({
  views: {
    get inspectionSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: self.apiSchema.properties
      };
    },
    get fertilizationSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: fertilizerTransformer(self.apiSchema.properties.fertilizers?.items?.properties || {})
      };
    },
    get harvestingSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: self.apiSchema.properties
      };
    },
    get plantingSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: plantingTransformer(self.apiSchema.properties)
      };
    },
    get soilObservationSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: self.apiSchema.properties
      };
    },
    get waterSupplySchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: self.apiSchema.properties
      };
    },
    get landPreparationSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: landPreparationTransformer(self.apiSchema.properties)
      };
    },
    get strawManagementSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: strawManagementTransformer(self.apiSchema.properties)
      };
    },
    get pestDiseaseSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: pestDiseaseTransformer(self.apiSchema.properties)
      };
    },
    get coverCropPlantingSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: coverCropPlantingTransformer(self.apiSchema.properties)
      };
    },
    get strawIncorporationSchema(): IAgroSchema {
      return {
        type: 'VerticalLayout',
        elements: strawIncorporationTransformer(self.apiSchema.properties)
      };
    }
  }
});